import {BASEURL, VITE_RESERVE_BASEURL, normalizeApiBaseUrl} from "@/http";

const API_IMG_PATH = "/img";
const TIMEOUT_MS = 3000;
const HEDGE_DELAY_MS = 400;
const INSTALLED_KEY = "__apiImgFallbackInstalled";

type ImgQueryValue = string | number | boolean | null | undefined;
type ImgQuery = Record<string, ImgQueryValue>;
declare global {
  interface Window {
    [INSTALLED_KEY]?: boolean;
  }
}

function trimSlash(value: string): string {
  return value.replace(/\/$/, "");
}

export function getImgBaseUrls(): string[] {
  return [BASEURL, VITE_RESERVE_BASEURL]
    .filter(Boolean)
    .map((baseUrl) => trimSlash(normalizeApiBaseUrl(baseUrl)) + API_IMG_PATH)
    .filter((baseUrl, index, baseUrls) => baseUrls.indexOf(baseUrl) === index);
}

function getAlternativeImgBaseUrl(): string {
  return getImgBaseUrls()[1] || "";
}

export function getApiImgUrls(url: string): string[] {
  if (!isApiImgUrl(url)) {
    return [];
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return getImgBaseUrls().map((baseUrl) => baseUrl + parsedUrl.search);
  } catch {
    return [];
  }
}

function buildQuery(query: ImgQuery = {}): string {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? "?" + queryString : "";
}

export function getApiImgUrl(
  query: ImgQuery = {},
  alternative = false
): string {
  const baseUrl = alternative
    ? getAlternativeImgBaseUrl()
    : trimSlash(BASEURL) + API_IMG_PATH;
  return baseUrl + buildQuery(query);
}

export function getApiImgCssUrl(
  query: ImgQuery = {},
  alternative = false
): string {
  return `url(${getApiImgUrl(query, alternative)})`;
}

export function getAlternativeApiImgUrl(url: string): string {
  return getApiImgUrls(url)[1] || "";
}

function loadFastestImage(urls: string[]): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const images: HTMLImageElement[] = [];
    const timers: number[] = [];
    let failedRequests = 0;
    let settled = false;

    const cleanup = (): void => {
      timers.forEach(window.clearTimeout);
      images.forEach((image) => {
        image.onload = null;
        image.onerror = null;
        if (!image.complete) {
          image.src = "";
        }
      });
    };

    const startRequest = (url: string): void => {
      if (settled) {
        return;
      }

      const image = new Image();
      images.push(image);
      let failed = false;

      const fail = (): void => {
        if (failed) {
          return;
        }

        failed = true;
        failedRequests += 1;
        if (!settled && failedRequests === urls.length) {
          settled = true;
          cleanup();
          reject(new Error("api/img request failed on all servers"));
        }
      };

      const timeout = window.setTimeout(fail, TIMEOUT_MS);
      timers.push(timeout);

      image.onload = (): void => {
        window.clearTimeout(timeout);
        if (settled) {
          return;
        }

        settled = true;
        cleanup();
        resolve(url);
      };
      image.onerror = (): void => {
        window.clearTimeout(timeout);
        fail();
      };
      image.src = url;
    };

    urls.forEach((url, index) => {
      if (index === 0) {
        startRequest(url);
      } else {
        timers.push(
          window.setTimeout(() => startRequest(url), index * HEDGE_DELAY_MS)
        );
      }
    });
  });
}

export async function resolveApiImgUrl(url: string): Promise<string> {
  const urls = getApiImgUrls(url);
  const alternativeUrl = urls[1] || "";

  if (!alternativeUrl) {
    return url;
  }

  try {
    return await loadFastestImage(urls);
  } catch {
    return alternativeUrl;
  }
}

export async function setBackgroundApiImg(
  element: HTMLElement | null,
  url: string
): Promise<void> {
  if (!element) {
    return;
  }

  element.style.backgroundImage = `url(${await resolveApiImgUrl(url)})`;
}

function isApiImgUrl(url: string): boolean {
  return (
    url.includes("/api/img") && !url.includes("blob:") && !url.includes("data:")
  );
}

function getFetchUrl(input: RequestInfo | URL): string {
  return typeof input === "string" || input instanceof URL
    ? String(input)
    : input.url;
}

function getFetchMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) {
    return init.method.toLowerCase();
  }

  return input instanceof Request ? input.method.toLowerCase() : "get";
}

function createFetchInputs(
  input: RequestInfo | URL,
  urls: string[]
): (RequestInfo | URL)[] {
  return urls.map((url) =>
    input instanceof Request ? new Request(url, input.clone()) : url
  );
}

async function fetchWithTimeout(
  originalFetch: typeof window.fetch,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  controller: AbortController
): Promise<Response> {
  const callerSignal =
    init?.signal || (input instanceof Request ? input.signal : undefined);
  const abort = (): void => controller.abort();
  const timeout = window.setTimeout(abort, TIMEOUT_MS);

  callerSignal?.addEventListener("abort", abort, {once: true});
  if (callerSignal?.aborted) {
    abort();
  }

  try {
    return await originalFetch(input, {...init, signal: controller.signal});
  } finally {
    window.clearTimeout(timeout);
    callerSignal?.removeEventListener("abort", abort);
  }
}

function fetchFastest(
  originalFetch: typeof window.fetch,
  inputs: (RequestInfo | URL)[],
  init?: RequestInit
): Promise<Response> {
  const controllers = inputs.map(() => new AbortController());
  const timers: number[] = [];

  return new Promise((resolve, reject) => {
    let failedRequests = 0;
    let settled = false;
    let lastError: unknown;
    let lastResponse: Response | undefined;

    const cleanup = (): void => {
      timers.forEach(window.clearTimeout);
      controllers.forEach((controller) => controller.abort());
    };

    const fail = (error?: unknown, response?: Response): void => {
      if (settled) {
        return;
      }

      lastError = error || lastError;
      lastResponse = response || lastResponse;
      failedRequests += 1;

      if (failedRequests === inputs.length) {
        settled = true;
        lastResponse ? resolve(lastResponse) : reject(lastError);
      }
    };

    inputs.forEach((input, index) => {
      const startRequest = (): void => {
        if (settled) {
          return;
        }

        fetchWithTimeout(originalFetch, input, init, controllers[index])
          .then((response) => {
            if (!response.ok) {
              fail(undefined, response);
              return;
            }

            if (!settled) {
              settled = true;
              cleanup();
              resolve(response);
            }
          })
          .catch(fail);
      };

      if (index === 0) {
        startRequest();
      } else {
        timers.push(window.setTimeout(startRequest, index * HEDGE_DELAY_MS));
      }
    });
  });
}

async function fetchInOrder(
  originalFetch: typeof window.fetch,
  inputs: (RequestInfo | URL)[],
  init?: RequestInit
): Promise<Response> {
  let lastError: unknown;

  for (const input of inputs) {
    try {
      const response = await fetchWithTimeout(
        originalFetch,
        input,
        init,
        new AbortController()
      );

      if (response.status !== 502 && response.status !== 504) {
        return response;
      }

      lastError = response;
    } catch (error) {
      const callerSignal =
        init?.signal || (input instanceof Request ? input.signal : undefined);
      if (callerSignal?.aborted) {
        throw error;
      }
      lastError = error;
    }
  }

  if (lastError instanceof Response) {
    return lastError;
  }
  throw lastError;
}

function installFetchFallback(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const url = getFetchUrl(input);
    const urls = getApiImgUrls(url);

    if (urls.length < 2) {
      return originalFetch(input, init);
    }

    const inputs = createFetchInputs(input, urls);
    return getFetchMethod(input, init) === "get"
      ? fetchFastest(originalFetch, inputs, init)
      : fetchInOrder(originalFetch, inputs, init);
  };
}

function installImgFallback(): void {
  const resolvingImages = new WeakSet<HTMLImageElement>();

  document.addEventListener(
    "error",
    (event) => {
      const target = event.target;

      if (
        !(target instanceof HTMLImageElement) ||
        !isApiImgUrl(target.src) ||
        resolvingImages.has(target)
      ) {
        return;
      }

      const urls = getApiImgUrls(target.src);
      const currentUrl = target.src;
      const currentIndex = urls.findIndex((url) => url === currentUrl);
      const fallbackUrls = urls.slice(Math.max(currentIndex + 1, 1));

      if (fallbackUrls.length === 0) {
        return;
      }

      resolvingImages.add(target);
      void loadFastestImage(fallbackUrls)
        .then((resolvedUrl) => {
          target.src = resolvedUrl;
        })
        .catch(() => undefined)
        .finally(() => resolvingImages.delete(target));
    },
    true
  );
}

function getBackgroundApiImgUrl(element: HTMLElement): string {
  const backgroundImage = element.style.backgroundImage;
  const match = backgroundImage.match(/url\(["']?([^"')]+)["']?\)/);
  const url = match?.[1] || "";

  return isApiImgUrl(url) ? url : "";
}

function installBackgroundFallback(): void {
  const handledElements = new WeakMap<HTMLElement, string>();

  const checkElement = (element: HTMLElement): void => {
    const url = getBackgroundApiImgUrl(element);

    if (!url || handledElements.get(element) === url) {
      return;
    }

    handledElements.set(element, url);
    void resolveApiImgUrl(url).then((resolvedUrl) => {
      if (resolvedUrl !== url && getBackgroundApiImgUrl(element) === url) {
        element.style.backgroundImage = `url(${resolvedUrl})`;
      }
    });
  };

  const scanElement = (element: Element): void => {
    if (element instanceof HTMLElement) {
      checkElement(element);
    }

    element
      .querySelectorAll<HTMLElement>("[style*='/api/img']")
      .forEach(checkElement);
  };

  let planned = false;
  const scanPage = (): void => {
    if (planned) {
      return;
    }

    planned = true;
    window.requestAnimationFrame(() => {
      planned = false;
      scanElement(document.body);
    });
  };

  scanPage();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === "attributes" &&
        mutation.target instanceof Element
      ) {
        scanElement(mutation.target);
        return;
      }

      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          scanElement(node);
        }
      });
    });
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["style"],
    childList: true,
    subtree: true
  });
}

export function installApiImgFallback(): void {
  if (window[INSTALLED_KEY]) {
    return;
  }

  if (!getAlternativeImgBaseUrl()) {
    return;
  }

  window[INSTALLED_KEY] = true;
  installFetchFallback();
  installImgFallback();
  void installBackgroundFallback;
}
